import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useSettingTemplates } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import { Combobox } from '../components/controls'

export interface RuleGroup {
  settingGroupTypeId: number
  settingGroupType: string
  displayOrderId: number
  templateTypeId: number
  templateType: string
  settingCount: number
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface RuleGroupPage {
  settingGroups: RuleGroup[]
  total: number
  offset: number
  limit: number
}

interface FormValue {
  settingGroupType: string
  displayOrderId: string
  templateTypeId: string
}

const emptyForm: FormValue = { settingGroupType: '', displayOrderId: '', templateTypeId: '' }

const PAGE_SIZE = 50

/**
 * RuleGroupsPage is the second level of the rules engine —
 * [Catalog].[Setting.GroupType]: the sections a settings form is split into.
 *
 * The position is a sort hint, not a key: seven production groups share one with
 * a sibling, because a template pairs "…Deposit" and "…Withdrawal" at the same
 * number. The list therefore orders by template, then position, then name, so a
 * tie still renders the same way twice.
 */
export function RuleGroupsPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { templates } = useSettingTemplates()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [templateFilter, setTemplateFilter] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<RuleGroup | null>(null)
  const [form, setForm] = useState<FormValue>(emptyForm)
  const [error, setError] = useState('')

  const params = {
    search,
    includeDisabled: String(includeDisabled),
    offset,
    limit: PAGE_SIZE,
    ...(templateFilter ? { templateTypeId: templateFilter } : {}),
  }

  const { data, isLoading } = useQuery({
    queryKey: ['setting-groups', params],
    queryFn: async (): Promise<RuleGroupPage> => {
      const res = await api.get('/setting-groups', { params })
      return res.data as RuleGroupPage
    },
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['setting-groups'] })
    // The template list leads with its group count, so it moves too.
    qc.invalidateQueries({ queryKey: ['setting-templates'] })
  }
  const set = (patch: Partial<FormValue>) => setForm((f) => ({ ...f, ...patch }))

  const openCreate = () => {
    setEditing(null)
    setForm({ ...emptyForm, templateTypeId: templateFilter })
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (row: RuleGroup) => {
    setEditing(row)
    setForm({
      settingGroupType: row.settingGroupType,
      displayOrderId: String(row.displayOrderId),
      templateTypeId: String(row.templateTypeId),
    })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        settingGroupType: form.settingGroupType.trim(),
        displayOrderId: Number(form.displayOrderId || 0),
        templateTypeId: Number(form.templateTypeId),
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing
        ? api.put(`/setting-groups/${editing.settingGroupTypeId}`, body)
        : api.post('/setting-groups', body)
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('ruleGroups.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (row: RuleGroup) =>
      row.isDisabledByPlatform
        ? api.post(`/setting-groups/${row.settingGroupTypeId}/restore`)
        : api.delete(`/setting-groups/${row.settingGroupTypeId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('ruleGroups.errors.save'))),
  })

  const groups = data?.settingGroups ?? []
  const total = data?.total ?? 0
  const templateItems = templates.map((tpl) => ({ value: String(tpl.id), label: tpl.name }))
  const canSubmit = form.settingGroupType.trim() !== '' && form.templateTypeId !== ''

  return (
    <div>
      <PageHeading
        title={t('ruleGroups.title')}
        subtitle={t('ruleGroups.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('ruleGroups.newGroup')}
                  </Button>
                }
              />
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editing ? t('ruleGroups.editGroup') : t('ruleGroups.newGroup')}
                  </DialogTitle>
                </DialogHeader>
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (canSubmit) save.mutate()
                  }}
                >
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="group-template">{t('ruleGroups.columns.template')}</Label>
                    <Combobox
                      value={form.templateTypeId}
                      onValueChange={(v) => set({ templateTypeId: v })}
                      items={templateItems}
                      placeholder={t('ruleGroups.pickTemplate')}
                      className="w-full"
                    />
                  </div>

                  <div className="flex gap-3">
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label htmlFor="group-name">{t('ruleGroups.columns.name')}</Label>
                      <Input
                        id="group-name"
                        value={form.settingGroupType}
                        onChange={(e) => set({ settingGroupType: e.target.value })}
                        maxLength={100}
                        placeholder="Crypto Methods Deposit"
                        autoFocus
                      />
                    </div>
                    <div className="flex w-28 flex-col gap-1.5">
                      <Label htmlFor="group-order">{t('ruleGroups.columns.order')}</Label>
                      <Input
                        id="group-order"
                        value={form.displayOrderId}
                        onChange={(e) =>
                          set({ displayOrderId: e.target.value.replace(/[^0-9]/g, '').slice(0, 3) })
                        }
                        placeholder="1"
                      />
                    </div>
                  </div>
                  <p className="-mt-2 text-xs text-muted-foreground">{t('ruleGroups.orderHint')}</p>

                  <ErrorBanner message={error} />

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" disabled={save.isPending || !canSubmit}>
                      {editing ? t('common.save') : t('common.create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="rule-groups">
        <p>{t('ruleGroups.help.line1')}</p>
        <p>{t('ruleGroups.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('ruleGroups.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled || templateFilter !== ''}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setTemplateFilter('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      >
        <Combobox
          value={templateFilter}
          onValueChange={(v) => {
            setTemplateFilter(v)
            setOffset(0)
          }}
          items={templateItems}
          placeholder={t('ruleGroups.allTemplates')}
          className="w-56"
        />
      </CatalogToolbar>

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('ruleGroups.columns.id')}</TableHead>
                  <TableHead className="w-20">{t('ruleGroups.columns.order')}</TableHead>
                  <TableHead>{t('ruleGroups.columns.name')}</TableHead>
                  <TableHead className="w-48">{t('ruleGroups.columns.template')}</TableHead>
                  <TableHead className="w-32">{t('ruleGroups.columns.settings')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((row) => (
                  <TableRow key={row.settingGroupTypeId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.settingGroupTypeId}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{row.displayOrderId}</TableCell>
                    <TableCell className="font-medium">{row.settingGroupType}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.templateType || `#${row.templateTypeId}`}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {t('ruleGroups.settingCount', { count: row.settingCount })}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={row.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(row)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={
                              row.isDisabledByPlatform ? t('common.restore') : t('common.retire')
                            }
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(row)}
                          >
                            {row.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {groups.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 7 : 6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('ruleGroups.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('ruleGroups.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
